import React from 'react'
import {
	format,
	startOfMonth,
	endOfMonth,
	eachDayOfInterval,
	isToday,
} from 'date-fns'
import { fr } from 'date-fns/locale'
import { X } from 'lucide-react'

interface CalendarModalProps {
	isCalendarModalVisible: boolean
	setIsCalendarModalVisible: (visible: boolean) => void
	availabilityData: any[]
	isLoading: boolean
	setSelectedDateFormate: (date: string) => void
	selectedDateFormate: string
	appointmentData: any[]
	minDate: Date
}

const CalendarModal: React.FC<CalendarModalProps> = ({
	isCalendarModalVisible,
	setIsCalendarModalVisible,
	availabilityData,
	isLoading,
	setSelectedDateFormate,
	selectedDateFormate,
	appointmentData,
	minDate,
}) => {
	if (!isCalendarModalVisible) return null

	const today = new Date()
	const [currentMonth, setCurrentMonth] = React.useState(today)

	const days = eachDayOfInterval({
		start: startOfMonth(currentMonth),
		end: endOfMonth(currentMonth),
	})

	const isDateAvailable = (date: Date) => {
		return availabilityData.some(
			availability => availability.date === format(date, 'yyyy-MM-dd')
		)
	}

	const getAvailableTimeSlots = (date: Date) => {
		const dateStr = format(date, 'yyyy-MM-dd')
		const availability = availabilityData.find(a => a.date === dateStr)
		return (
			availability?.timeSlots.filter((slot: any) => slot.isAvailable) ||
			[]
		)
	}

	const handleDateClick = (date: Date) => {
		if (isDateAvailable(date)) {
			setSelectedDateFormate(format(date, 'yyyy-MM-dd'))
		}
	}

	const handlePrevMonth = () => {
		setCurrentMonth(prev => {
			const newDate = new Date(prev)
			newDate.setMonth(prev.getMonth() - 1)
			return newDate
		})
	}

	const handleNextMonth = () => {
		setCurrentMonth(prev => {
			const newDate = new Date(prev)
			newDate.setMonth(prev.getMonth() + 1)
			return newDate
		})
	}

	const selectedDate = selectedDateFormate
		? new Date(selectedDateFormate)
		: null
	const availableTimeSlots = selectedDate
		? getAvailableTimeSlots(selectedDate)
		: []

	return (
		<div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
			<div className="w-full max-w-md rounded-[32px] bg-white p-0 shadow-lg">
				{/* Header Bar */}
				<div className="border-b-white70 flex items-center justify-between rounded-t-[32px] border-b px-6 py-4">
					<button
						onClick={handlePrevMonth}
						className="rounded-full p-2 hover:bg-gray-100"
						aria-label="Mois précédent">
						<svg
							width="24"
							height="24"
							viewBox="0 0 24 24"
							fill="none">
							<path
								d="M15 18L9 12L15 6"
								stroke="#160E3B"
								strokeWidth="2"
								strokeLinecap="round"
								strokeLinejoin="round"
							/>
						</svg>
					</button>
					<h2 className="font-Outfit text-primary text-[20px] font-medium">
						{format(currentMonth, 'MMMM yyyy', { locale: fr })}
					</h2>
					<div className="flex items-center gap-2">
						<button
							onClick={handleNextMonth}
							className="rounded-full p-2 hover:bg-gray-100"
							aria-label="Mois suivant">
							<svg
								width="24"
								height="24"
								viewBox="0 0 24 24"
								fill="none">
								<path
									d="M9 6L15 12L9 18"
									stroke="#160E3B"
									strokeWidth="2"
									strokeLinecap="round"
									strokeLinejoin="round"
								/>
							</svg>
						</button>
						<button
							onClick={() => setIsCalendarModalVisible(false)}
							className="ml-2 rounded-full p-2 hover:bg-gray-100"
							aria-label="Fermer">
							<X
								size={20}
								className="text-primary"
							/>
						</button>
					</div>
				</div>

				{/* Calendar Grid */}
				<div className="px-6 py-4">
					<div className="mb-2 grid grid-cols-7 gap-1">
						{['Dim', 'Lun', 'Mar', 'Mer', 'Jeu', 'Ven', 'Sam'].map(
							day => (
								<div
									key={day}
									className="font-Outfit text-primary text-center text-sm font-medium">
									{day}
								</div>
							)
						)}
						{days.map(day => {
							const isAvailable = isDateAvailable(day)
							const isSelected =
								format(day, 'yyyy-MM-dd') ===
								selectedDateFormate
							const todayStart = new Date(
								today.getFullYear(),
								today.getMonth(),
								today.getDate()
							).getTime()
							const isBeforeToday = day.getTime() < todayStart
							const isDisabled = day < minDate || isBeforeToday

							return (
								<button
									key={day.toString()}
									onClick={() => handleDateClick(day)}
									disabled={!isAvailable || isDisabled}
									className={`font-Outfit relative flex h-12 w-12 items-center justify-center rounded-full text-base transition-colors duration-150 ${
										isSelected
											? 'bg-yellow text-primary border-yellow border-2 font-bold'
											: isAvailable && !isDisabled
												? 'text-darkPrimary border-white80 border bg-white hover:bg-gray-100'
												: 'border-white80 cursor-not-allowed border bg-gray-200 text-gray-400 opacity-50'
									} ${isToday(day) ? 'ring-primary ring-2' : ''} `}>
									{format(day, 'd')}
									{isAvailable && !isDisabled && (
										<span className="bg-yellow absolute bottom-2 left-1/2 h-2 w-2 -translate-x-1/2 rounded-full" />
									)}
								</button>
							)
						})}
					</div>

					{/* Time Slots */}
					{selectedDate && availableTimeSlots.length > 0 && (
						<div className="mt-6">
							<h3 className="font-Outfit text-primary mb-3 text-lg font-medium">
								Horaires disponibles pour le{' '}
								{format(selectedDate, 'dd MMMM yyyy', {
									locale: fr,
								})}
							</h3>
							<div className="grid grid-cols-2 gap-2">
								{availableTimeSlots.map((slot: any) => (
									<div
										key={slot.time}
										className="bg-primary font-Outfit rounded-xl p-3 text-center text-base text-white shadow-sm">
										{slot.time}
									</div>
								))}
							</div>
						</div>
					)}
				</div>
			</div>
		</div>
	)
}

export default CalendarModal
